import React from 'react';
import Box from '@mui/joy/Box';
import Sheet from '@mui/joy/Sheet';
import Typography from '@mui/joy/Typography';
import Table from '@mui/joy/Table';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import Button from '@mui/joy/Button';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getProxyStatus, reloadInventorySyncProxies } from '../../inventorySyncApi';
import { PROXY_HINTS, RELOAD_HINT } from '../../inventorySyncHints';
import { ProxyStatus } from '../../../../models/inventorySync';
import { formatUtcDateTime } from '../../../../common/dateTime';
import ConfirmDialog from '../../../../components/confirmDialog/ConfirmDialog';
import { NotificationService } from '../../../../common/notification';
import { HintHeader, OverlayTooltip } from './shared';
import {
    formatAgo,
    formatNextRequest,
    formatRequestsPerMinute,
    formatTimeLeft,
    formatUsedBy,
    getProxyStatusLabel,
    sortProxies,
} from './proxyUtils';

/** Текущее время, обновляется раз в секунду, пока enabled — для отсчёта пауз и cooldown */
function useNow(enabled: boolean): number {
    const [now, setNow] = React.useState(() => Date.now());
    React.useEffect(() => {
        if (!enabled) {
            return undefined;
        }
        setNow(Date.now());
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, [enabled]);
    return now;
}

/** Запросов за прошлую минуту / лимит; выше лимита — красным */
function RequestsPerMinuteText(props: { proxy: ProxyStatus }) {
    const { text, overLimit } = formatRequestsPerMinute(props.proxy.requestsLastMinute, props.proxy.requestsPerMinuteLimit);
    return overLimit
        ? <Typography component="span" color="danger" fontWeight="lg">{text}</Typography>
        : <>{text}</>;
}

/** Значение с точным временем (UTC → локальное) во всплывающей подсказке */
function TimeCell(props: { text: string; at?: string | null }) {
    if (!props.at || props.text === '—') {
        return <>{props.text}</>;
    }
    return (
        <OverlayTooltip title={formatUtcDateTime(props.at)} variant="outlined">
            <span>{props.text}</span>
        </OverlayTooltip>
    );
}

export function ProxiesSubTab(props: { isActive: boolean }) {
    const { isActive } = props;
    const [loading, setLoading] = React.useState(false);
    const [reloading, setReloading] = React.useState(false);
    const [proxies, setProxies] = React.useState<ProxyStatus[]>([]);
    const [error, setError] = React.useState<string | null>(null);
    const [loaded, setLoaded] = React.useState(false);
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const autoLoadedRef = React.useRef(false);

    const loadProxies = React.useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getProxyStatus();
            if (result.result && result.value) {
                setProxies(result.value);
                setLoaded(true);
            } else {
                setError('Не удалось загрузить состояние прокси');
            }
        } catch {
            setError('Ошибка запроса списка прокси');
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        if (isActive && !autoLoadedRef.current) {
            autoLoadedRef.current = true;
            void loadProxies();
        }
    }, [isActive, loadProxies]);

    const confirmReload = React.useCallback(async () => {
        setConfirmOpen(false);
        setReloading(true);
        try {
            const res = await reloadInventorySyncProxies();
            if (res.result && res.value) {
                NotificationService.alertSuccess(
                    `Прокси перезагружены: ${res.value.activeProxyCount} из ${res.value.proxyCount} активны`
                );
                await loadProxies();
            } else {
                const msg = typeof res.value === 'string' ? res.value : 'Не удалось перезагрузить прокси';
                NotificationService.alertError(msg);
            }
        } finally {
            setReloading(false);
        }
    }, [loadProxies]);

    const activeProxies = React.useMemo(
        () => proxies.filter(p => !p.isDisabled).length,
        [proxies]
    );

    const sortedProxies = React.useMemo(
        () => sortProxies(proxies),
        [proxies]
    );

    const busyProxiesCount = React.useMemo(
        () => proxies.filter(p => p.isBusy).length,
        [proxies]
    );

    const now = useNow(isActive && loaded);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1, minHeight: 0 }}>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1, justifyContent: { sm: 'flex-end' } }}>
                <OverlayTooltip title={RELOAD_HINT} variant="outlined">
                    <Button
                        size="sm"
                        variant="outlined"
                        color="warning"
                        startDecorator={<RefreshIcon />}
                        onClick={() => setConfirmOpen(true)}
                        disabled={loading || reloading}
                        loading={reloading}
                        sx={{ width: { xs: '100%', sm: 'auto' } }}
                    >
                        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Перезагрузить прокси</Box>
                        <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Перезагрузить</Box>
                    </Button>
                </OverlayTooltip>
                <Button
                    size="sm"
                    variant="outlined"
                    startDecorator={<KeyboardArrowRightIcon />}
                    onClick={loadProxies}
                    disabled={loading || reloading}
                    loading={loading}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    Обновить
                </Button>
            </Box>

            <ConfirmDialog
                title="Подтверждение"
                open={confirmOpen}
                setOpen={setConfirmOpen}
                onConfirm={confirmReload}
            >
                Перезагрузить пул прокси и сбросить адаптивный лимит параллелизма?
            </ConfirmDialog>

            {loading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size="sm" />
                    <Typography level="body-sm">Загрузка...</Typography>
                </Box>
            )}

            {!loading && error && (
                <Typography color="danger" level="body-md">{error}</Typography>
            )}

            {!loading && !error && loaded && (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, flex: 1, minHeight: 0 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography level="title-md" sx={{ fontSize: { xs: 'md', sm: 'lg' } }}>
                            Прокси ({activeProxies}/{proxies.length}
                            {busyProxiesCount > 0 ? `, ${busyProxiesCount} в работе` : ''})
                        </Typography>
                        <Typography level="body-xs" color="neutral" sx={{ display: { xs: 'none', sm: 'block' } }}>
                            Сортировка: в работе → по score → отключённые внизу
                        </Typography>
                    </Box>

                    <Sheet
                        variant="outlined"
                        sx={{
                            p: 2,
                            borderRadius: 'sm',
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            flex: 1,
                            minHeight: 0,
                            overflow: 'hidden',
                        }}
                    >
                        <Box sx={{ overflow: 'auto', flex: 1, minHeight: 0 }}>
                            <Table borderAxis="bothBetween" size="sm" stickyHeader sx={{ '--TableCell-headFontWeight': 600 }}>
                                <thead>
                                    <tr>
                                        <HintHeader label="ID" hint={PROXY_HINTS.id} />
                                        <HintHeader label="Статус" hint={PROXY_HINTS.status} />
                                        <HintHeader label="Следующий запрос" hint={PROXY_HINTS.nextRequestAt} />
                                        <HintHeader label="Cooldown" hint={PROXY_HINTS.restLeft} />
                                        <HintHeader label="Последний 429" hint={PROXY_HINTS.lastRateLimitedAt} />
                                        <HintHeader label="Запросов/мин" hint={PROXY_HINTS.requestsLastMinute} />
                                        <HintHeader label="Кто использовал" hint={PROXY_HINTS.usedBy} />
                                        <HintHeader label="Score" hint={PROXY_HINTS.score} />
                                        <HintHeader label="Успехи" hint={PROXY_HINTS.successCount} />
                                        <HintHeader label="429" hint={PROXY_HINTS.rateLimitCount} />
                                        <HintHeader label="Ошибки" hint={PROXY_HINTS.failureCount} />
                                        <HintHeader label="Подряд 429" hint={PROXY_HINTS.consecutive429} />
                                        <HintHeader label="Подряд ошибок" hint={PROXY_HINTS.consecutiveProxyFailures} />
                                        <HintHeader label="Отключён" hint={PROXY_HINTS.disabledAt} />
                                        <HintHeader label="Причина" hint={PROXY_HINTS.disabledReason} />
                                    </tr>
                                </thead>
                                <tbody>
                                    {sortedProxies.map((proxy) => {
                                        const status = getProxyStatusLabel(proxy);
                                        const rowStyle = proxy.isBusy
                                            ? { backgroundColor: 'var(--joy-palette-primary-softBg)' }
                                            : proxy.isDisabled
                                                ? { backgroundColor: 'var(--joy-palette-danger-softBg)' }
                                                : undefined;
                                        return (
                                            <tr key={proxy.id} style={rowStyle}>
                                                <td>{proxy.id}</td>
                                                <td>
                                                    <OverlayTooltip title={status.hint} variant="outlined">
                                                        <Chip variant="soft" color={status.color} size="sm">{status.label}</Chip>
                                                    </OverlayTooltip>
                                                </td>
                                                <td><TimeCell text={formatNextRequest(proxy.nextRequestAt, now)} at={proxy.nextRequestAt} /></td>
                                                <td><TimeCell text={formatTimeLeft(proxy.restUntil, now)} at={proxy.restUntil} /></td>
                                                <td><TimeCell text={formatAgo(proxy.lastRateLimitedAt, now)} at={proxy.lastRateLimitedAt} /></td>
                                                <td><RequestsPerMinuteText proxy={proxy} /></td>
                                                <td>{formatUsedBy(proxy.usedBy)}</td>
                                                <td>{proxy.score}</td>
                                                <td>{proxy.successCount}</td>
                                                <td>{proxy.rateLimitCount}</td>
                                                <td>{proxy.failureCount}</td>
                                                <td>{proxy.consecutive429}</td>
                                                <td>{proxy.consecutiveProxyFailures}</td>
                                                <td>{formatUtcDateTime(proxy.disabledAt)}</td>
                                                <td>{proxy.disabledReason || '—'}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </Box>
                    </Sheet>

                    <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1, flex: 1, minHeight: 0, overflow: 'auto', pr: 0.5 }}>
                        {sortedProxies.map((proxy) => {
                            const status = getProxyStatusLabel(proxy);
                            return (
                                <Sheet
                                    key={proxy.id}
                                    variant="outlined"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 'sm',
                                        flexShrink: 0,
                                        ...(proxy.isBusy ? { bgcolor: 'primary.softBg' } : {}),
                                        ...(proxy.isDisabled ? { bgcolor: 'danger.softBg' } : {}),
                                    }}
                                >
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1, gap: 1 }}>
                                        <Typography level="title-sm">#{proxy.id}</Typography>
                                        <OverlayTooltip title={status.hint} variant="outlined">
                                            <Chip variant="soft" color={status.color} size="sm">{status.label}</Chip>
                                        </OverlayTooltip>
                                    </Box>
                                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 1 }}>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">Следующий запрос</Typography>
                                            <Typography level="body-sm">{formatNextRequest(proxy.nextRequestAt, now)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">Последний 429</Typography>
                                            <Typography level="body-sm">{formatAgo(proxy.lastRateLimitedAt, now)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">Запросов/мин</Typography>
                                            <Typography level="body-sm"><RequestsPerMinuteText proxy={proxy} /></Typography>
                                        </Box>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">Кто использовал</Typography>
                                            <Typography level="body-sm">{formatUsedBy(proxy.usedBy)}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">Score</Typography>
                                            <Typography level="body-sm">{proxy.score}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">Успехи</Typography>
                                            <Typography level="body-sm">{proxy.successCount}</Typography>
                                        </Box>
                                        <Box>
                                            <Typography level="body-xs" color="neutral">429 / ошибки</Typography>
                                            <Typography level="body-sm">{proxy.rateLimitCount} / {proxy.failureCount}</Typography>
                                        </Box>
                                    </Box>
                                    {(proxy.isResting || proxy.isDisabled || proxy.disabledReason) && (
                                        <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid', borderColor: 'divider', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                            {proxy.isResting && (
                                                <Typography level="body-xs">
                                                    Cooldown: {formatTimeLeft(proxy.restUntil, now)} (до {formatUtcDateTime(proxy.restUntil)})
                                                </Typography>
                                            )}
                                            {proxy.isDisabled && (
                                                <Typography level="body-xs">Отключён: {formatUtcDateTime(proxy.disabledAt)}</Typography>
                                            )}
                                            {proxy.disabledReason && (
                                                <Typography level="body-xs" color="danger">{proxy.disabledReason}</Typography>
                                            )}
                                        </Box>
                                    )}
                                </Sheet>
                            );
                        })}
                    </Box>

                    {proxies.length === 0 && (
                        <Typography level="body-md" color="neutral">Прокси не найдены</Typography>
                    )}
                </Box>
            )}
        </Box>
    );
}

export default ProxiesSubTab;
